import type { PaymentBrief } from "@/features/finance/types";
import type { DocumentsSummary } from "@/features/documents/types";
/** Backend serializer'lariga mos turlar: apps/drivers/serializers.py */

export type DriverStatus =
  | "new"
  | "contacted"
  | "qualified"
  | "docs"
  | "submitted"
  | "background_check"
  | "orientation"
  | "hired"
  | "rejected"
  | "not_interested"
  | "quit";

export const PIPELINE_ORDER: DriverStatus[] = ["new", "contacted", "qualified", "docs", "submitted", "background_check", "orientation", "hired"];

/** Muammoli driverlar (backend apps/drivers/problems.py): ro'yxat filtri `?problem=` va qatordagi belgi */
export const DRIVER_PROBLEMS = ["incomplete", "docs_missing", "docs_expiring", "docs_expired", "overdue_reminder", "no_contact", "stale", "unassigned"] as const;
export type DriverProblem = (typeof DRIVER_PROBLEMS)[number];
export const LOST_STATUSES: DriverStatus[] = ["rejected", "not_interested", "quit"];
export const SUBMITTABLE_STATUSES: DriverStatus[] = ["qualified", "docs"];

export const STATUS_LABEL: Record<DriverStatus, string> = {
  new: "New",
  contacted: "Contacted",
  qualified: "Qualified",
  docs: "Docs",
  submitted: "Submitted",
  background_check: "Background check",
  orientation: "Orientation",
  hired: "Hired",
  rejected: "Rejected",
  not_interested: "Not interested",
  quit: "Quit",
};

export type Source = "facebook" | "indeed" | "referral" | "craigslist" | "telegram" | "apply_form" | "import" | "other";
export const SOURCE_LABEL: Record<Source, string> = {
  facebook: "Facebook",
  indeed: "Indeed",
  referral: "Referral",
  craigslist: "Craigslist",
  telegram: "Telegram",
  apply_form: "Apply form",
  import: "Sheets import",
  other: "Other",
};

export type LegalStatus = "citizen" | "green_card" | "work_permit" | "non_domiciled" | "other";
export const LEGAL_STATUS_LABEL: Record<LegalStatus, string> = {
  citizen: "US Citizen",
  green_card: "Green Card",
  work_permit: "Work Permit (EAD)",
  non_domiciled: "Non-domiciled",
  other: "Other",
};

export interface UserRef {
  id: number;
  display_name: string;
  initials: string;
}

export interface JobRef {
  id: number;
  title: string;
  carrier_id: number;
  carrier_name: string;
  status: "open" | "filled" | "closed";
  states: string[];
}

export interface NextReminder {
  id: number;
  kind: ReminderKind;
  due_at: string;
  note: string;
  overdue: boolean;
}

/** Mijoz jadvalidagi "Type of drivers". Umumiy qiymatlar (company, lease, owner, team) -- eski yozuvlar, solo/team noma'lum */
export const DRIVER_TYPES = ["company_solo", "company_team", "lease_solo", "lease_team", "owner_solo", "owner_team", "training", "co_driver"] as const;
export const LEGACY_DRIVER_TYPES = ["company", "lease", "owner", "team"] as const;
export type DriverType = (typeof DRIVER_TYPES)[number] | (typeof LEGACY_DRIVER_TYPES)[number];
/** Yuk turi ("Recruiting type"): Amazon Relay yoki street; vakansiyada ikkalasi ham bo'lishi mumkin */
export type FreightType = "amazon" | "street" | "both";
export type FeeStatus = "pending" | "paid" | "partial" | "not_paid";

export interface DriverListItem {
  id: number;
  full_name: string;
  first_name: string;
  last_name: string;
  phone: string;
  phone_display: string;
  state: string;
  city: string;
  cdl_class: string;
  driver_type: DriverType | "";
  freight_type: FreightType | "";
  experience_months: number | null;
  experience_label: string;
  status: DriverStatus;
  status_changed_at: string;
  status_reason: string;
  days_in_stage: number;
  source: Source;
  legal_status?: LegalStatus | "";
  owner: UserRef | null;
  assigned_until: string | null;
  branch_id: number | null;
  last_contact_at: string | null;
  current_job: JobRef | null;
  cdl_expires: string | null;
  medical_card_expires: string | null;
  next_reminder: NextReminder | null;
  created_at: string;
  version: number;
  /** Muammo turlari (faqat ro'yxatda hisoblanadi; doskada bo'sh) */
  problems?: DriverProblem[];
}

export type ApplicationStatus = "submitted" | "background_check" | "orientation" | "hired" | "rejected" | "withdrawn";

export interface ApplicationBrief {
  id: number;
  job: JobRef;
  status: ApplicationStatus;
  submitted_by: UserRef | null;
  submitted_at: string;
  note: string;
  carrier_reply: string;
  decided_at: string | null;
  rejection_reason: string;
  start_date: string | null;
  pay_plan: string;
  /** Carrier to'lovi: ishga olinganda boshlanadi; summa faqat "budget" ruxsati bilan keladi */
  fee_status: FeeStatus | "";
  fee_amount: string | null;
  fee_due_date: string | null;
  fee_paid_at: string | null;
  fee_note: string;
  /** Tushumlar va qoldiq (faqat "budget" ruxsati bilan), hire tasdiqlanganmi */
  received: PaymentBrief[];
  received_total: string | null;
  remaining: string | null;
  approved: boolean;
  version: number;
}

export interface DriverDetail extends DriverListItem {
  phone_secondary: string;
  phone_secondary_display: string;
  email: string;
  date_of_birth: string | null;
  date_of_birth_masked: string;
  referred_by: string;
  cdl_state: string;
  cdl_number: string;
  cdl_number_masked: string;
  driving_type: string;
  endorsements: string[];
  equipment: string[];
  home_time: string;
  desired_pay: string;
  available_from: string | null;
  notes: string;
  hired_job: JobRef | null;
  start_date: string | null;
  hire_approved_at: string | null;
  hire_approved_by: UserRef | null;
  retention_30_at: string | null;
  retention_60_at: string | null;
  retention_90_at: string | null;
  applications: ApplicationBrief[];
  activity_count: number;
  import_ref: string;
  can_edit_sensitive: boolean;
  updated_at: string;
  documents_summary: DocumentsSummary;
}

export interface DriverWrite {
  /** Dublikat ogohlantirishidan keyin "bu boshqa odam -- baribir qo'shish" (faqat yaratishda) */
  confirm_duplicate?: boolean;
  first_name: string;
  last_name: string;
  phone: string;
  phone_secondary?: string;
  email?: string;
  state?: string;
  city?: string;
  date_of_birth?: string | null;
  source: Source;
  referred_by?: string;
  legal_status?: LegalStatus | "";
  cdl_class?: string;
  cdl_state?: string;
  cdl_number?: string;
  cdl_expires?: string | null;
  medical_card_expires?: string | null;
  experience_months?: number | null;
  driving_type?: string;
  endorsements?: string[];
  equipment?: string[];
  home_time?: string;
  desired_pay?: string;
  available_from?: string | null;
  notes?: string;
  owner?: number | null;
  driver_type?: DriverType | "";
  freight_type?: FreightType | "";
}

export type ActivityType = "call" | "note" | "sms" | "email" | "status" | "system";

export interface Activity {
  id: number;
  type: ActivityType;
  body: string;
  duration_sec: number | null;
  outcome: string;
  created_by: UserRef | null;
  created_at: string;
}

export interface StatusHistoryItem {
  id: number;
  from_status: DriverStatus | "";
  to_status: DriverStatus;
  reason: string;
  note: string;
  job: JobRef | null;
  changed_by: UserRef | null;
  changed_at: string;
}

export type ReminderKind = "call_back" | "send_docs" | "follow_up" | "cdl_expiry" | "medical_expiry" | "retention_check";
export const REMINDER_LABEL: Record<ReminderKind, string> = {
  call_back: "Call back",
  send_docs: "Send docs",
  follow_up: "Follow up",
  cdl_expiry: "CDL expiry",
  medical_expiry: "Medical card expiry",
  retention_check: "Retention check",
};

export interface Reminder {
  id: number;
  driver: number;
  driver_name: string;
  driver_status: DriverStatus;
  driver_state: string;
  driver_phone: string;
  kind: ReminderKind;
  due_at: string;
  effective_due: string;
  note: string;
  assigned_to: UserRef | null;
  auto: boolean;
  done_at: string | null;
  snoozed_until: string | null;
  is_done: boolean;
  overdue: boolean;
  created_at: string;
}

export interface DuplicateInfo {
  id: number;
  full_name: string;
  /** Doiradan tashqari driverda yo'q (faqat ism, egasi va scam belgisi) */
  status?: DriverStatus;
  status_reason?: string;
  status_changed_at?: string;
  /** Qachondir "Scam" sababi bilan yo'qotilgan (status tarixidan; qayta ochilsa ham qoladi). Doiradan tashqarida -- faqat sana */
  scam: { at: string; status?: DriverStatus; by?: string | null } | null;
  owner: { id: number; display_name: string } | null;
  assigned_until: string | null;
  phone: string;
  branch_id: number | null;
  can_open: boolean;
  /** Nima bo'yicha topildi: telefon va CDL -- doim bloklaydi; qo'shimcha telefon, email -- siyosatga qarab; name -- faqat ogohlantirish */
  match: "phone" | "phone_secondary" | "cdl" | "email" | "name";
}

/** POST /api/drivers/check-duplicate/ (backend drivers.services.find_strong_duplicate / find_name_matches) */
export interface DuplicateCheckResult {
  duplicate: DuplicateInfo | null;
  /** Bir xil ism-familiyali driverlar (kuchli moslik bo'lmasa) */
  name_matches: DuplicateInfo[];
  /** Kuchli moslikda shu foydalanuvchi uchun: block -- qo'shib bo'lmaydi, warn -- tasdiq bilan qo'sha oladi */
  policy: "block" | "warn";
  phone_normalized: string | null;
}

export interface StatusChangeInput {
  status: DriverStatus;
  reason?: string;
  note?: string;
  job?: number | null;
  start_date?: string | null;
  version: number;
}

export interface PipelineColumn {
  status: DriverStatus;
  count: number;
  drivers: DriverListItem[];
}

export type StatusCounts = Partial<Record<DriverStatus | "all" | "lost", number>>;

export const ENDORSEMENTS = ["Tanker", "Hazmat", "Doubles/Triples", "Passenger", "School bus"];
export const EQUIPMENT = ["Dry van", "Reefer", "Flatbed", "Tanker", "Car hauler", "Step deck"];
export const HOME_TIME = ["Daily", "Weekly", "Every 2 weeks", "Every 3 weeks", "Monthly"];
export const DRIVING_TYPES = ["OTR", "Regional", "Local", "Dedicated"];
export const US_STATES = [
  "AL", "AK", "AZ", "AR", "CA", "CO", "CT", "DE", "FL", "GA", "HI", "ID", "IL", "IN", "IA", "KS", "KY", "LA", "ME", "MD", "MA", "MI", "MN", "MS", "MO", "MT", "NE", "NV", "NH", "NJ", "NM", "NY", "NC", "ND", "OH", "OK", "OR", "PA", "RI", "SC", "SD", "TN", "TX", "UT", "VT", "VA", "WA", "WV", "WI", "WY", "DC",
];

/** Qo'ng'iroq natijasi (Activity.outcome); yorliq: drivers:profile.activity.outcomes.<key> */
export const CALL_OUTCOMES = ["answered", "no_answer", "voicemail", "busy"] as const;
export type CallOutcome = (typeof CALL_OUTCOMES)[number];
